import { Theme, createStyles, makeStyles } from '@material-ui/core';
import { NetworkIndicator, VerticalStepper } from 'design-system';
import { useEffect, useMemo, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';

// Data Layer
import {
  EMITTED,
  PENDING_EMISSION,
  PENDING_INSPECTION,
  PROCESSING_EMISSION,
} from 'data-layer-cr/dist/constants/quotationStatus';
import { coverageInfoForProduct } from 'data-layer-cr/dist/functions/coverages';
import { matchCurrentDate } from 'data-layer-cr/dist/functions/helpers';
import { isInspectionRequired } from 'data-layer-cr/dist/functions/quotations';
import { ProductCoverageCatalog, Quotation } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';

// Redux related
import { requestCoveragesForProduct } from '../../../../../../store/coverage/actions';
import {
  getById,
  getChangesForQuotationId,
} from '../../../../../../store/quotation/actions';
import { GET_DETAILS } from '../../../../../../store/quotation/requestIdentifiers';
import { RootState } from '../../../../../../store/rootReducer';

// Models
import QuotationChange from '../../../../../../models/Quotation/QuotationChange';

// Functions
import { hasUnits } from '../../../../../../functions/quotationMethods';

// Components
import { QuotationDetailsDialogue } from '../../../Details/components';
import {
  DetailsStepCard,
  EmissionStepCard,
  InspectionStepCard,
} from '../../components';
import PolicyDocumentManager from './PolicyDocumentManager';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    content: {
      padding: theme.spacing(2),
    },
  }),
);

function getStepOffset(
  quotationStatusId: number,
  inspectionRequired: boolean,
): number {
  switch (quotationStatusId) {
    case PENDING_INSPECTION:
      return 1;
    case PENDING_EMISSION:
    case PROCESSING_EMISSION:
    case EMITTED:
      return inspectionRequired ? 2 : 1;
    default:
      return 0;
  }
}

export const QuotationHistoryContent = ({
  quotation,
  coveragesForProduct,
  changes,
  getByIdAction,
  requestCoveragesForProductAction,
  getChangesForQuotationIdAction,
  quotationHash,
}: Props): JSX.Element => {
  const [detailsOpen, toggleDetailsOpen] = useState(false);
  const requestStatus = useNetworkObserver(GET_DETAILS);
  const classes = useStyles();
  const { configuration } = useMemo(
    () => coverageInfoForProduct(coveragesForProduct, quotation.productId),
    [coveragesForProduct, quotation],
  );

  /**
   * Don't try to update if the provided id is -1, since this means that we
   * provided an "invalid" quotation.
   */
  useEffect(() => {
    if (quotationHash !== '') {
      getByIdAction(quotationHash);
      getChangesForQuotationIdAction(quotationHash);
    }
  }, [quotationHash, getByIdAction, getChangesForQuotationIdAction]);

  /**
   * We need the product configuration related to this quotation. Only refresh it
   * if there is no configuration defined for the specified product.
   */
  useEffect(() => {
    if (quotation.productId !== '' && hasUnits(quotation)) {
      requestCoveragesForProductAction(
        quotation.productId,
        quotation.units[0].unit.use,
      );
    }
  }, [quotation, requestCoveragesForProductAction]);

  const goToDetailsCard = useMemo(
    () => (
      <DetailsStepCard
        data-testid="details-card"
        changes={changes}
        onClick={(): void => toggleDetailsOpen(true)}
      />
    ),
    [changes, toggleDetailsOpen],
  );

  const inspectionCard = useMemo(
    () => (
      <InspectionStepCard
        data-testid="inspection-card"
        quotation={quotation}
        changes={changes}
      />
    ),
    [quotation, changes],
  );

  const emissionCard = useMemo(
    () => (
      <EmissionStepCard
        data-testid="emission-card"
        quotation={quotation}
        changes={changes}
      />
    ),
    [quotation, changes],
  );

  /**
   * new vehicles do not need inspection. Check with isInspectionRequired
   */
  const inspectionRequired: boolean = useMemo(
    () => isInspectionRequired(quotation, configuration.inspectionRequired),
    [quotation, configuration],
  );

  /**
   * Which step should be considered as "active" depending on the quotation
   * state. Note that it is not a 1 to 1 relationship. Will adjust to the
   * product configuration.
   *
   * When quotatin is expired, it needs to render at 0 so that
   * the VerticalStepper dotted line form always starts in the first step
   */
  const stepOffset = useMemo(() => {
    if (matchCurrentDate(quotation.expireAt)) {
      return 0;
    }
    return getStepOffset(quotation.status.id, inspectionRequired);
  }, [quotation, inspectionRequired]);

  /**
   * The used steps. Will adjust to the product configuration.
   */
  const cardSteps = useMemo(
    () =>
      inspectionRequired
        ? [goToDetailsCard, inspectionCard, emissionCard]
        : [goToDetailsCard, emissionCard],
    [inspectionRequired, goToDetailsCard, inspectionCard, emissionCard],
  );

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <NetworkIndicator
          data-testid="network-indicator"
          requesting={requestStatus === 'requesting' || configuration.id === -1}
          message="Obteniendo detalle de cotización..."
        />
        {requestStatus !== 'requesting' &&
          hasUnits(quotation) &&
          configuration.id !== -1 && (
            <>
              {quotation.status.id === EMITTED ? (
                <PolicyDocumentManager
                  data-testid="digital-file-manager"
                  quotation={quotation}
                  inspectionRequired={inspectionRequired}
                />
              ) : (
                <VerticalStepper
                  data-testid="stepper"
                  steps={cardSteps}
                  activeStep={stepOffset}
                  /** only show disabled version(with dotted lines) if the quotation is expired */
                  disabled={matchCurrentDate(quotation.expireAt)}
                />
              )}
            </>
          )}
      </div>

      <QuotationDetailsDialogue
        data-testid="details-dialogue"
        quotation={quotation}
        open={detailsOpen}
        onCloseRequested={(): void => toggleDetailsOpen(false)}
      />
    </div>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  quotation: Quotation;
  changes: QuotationChange[];
  coveragesForProduct: ProductCoverageCatalog;
} => ({
  quotation: state.quotation.currentQuotation,
  changes: state.quotation.currentQuotationChanges,
  coveragesForProduct: state.coverage.coveragesForProduct,
});

const reduxActions = {
  getByIdAction: getById,
  getChangesForQuotationIdAction: getChangesForQuotationId,
  requestCoveragesForProductAction: requestCoveragesForProduct,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  quotationHash: string;
}

export default connector(QuotationHistoryContent);
